// src/components/ProductImagesList.jsx
import React, { useMemo, useState, useEffect } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from "@tanstack/react-table";

function useLocalStorage(key, initial) {
  const [state, setState] = useState(() => {
    try { const s = localStorage.getItem(key); return s ? JSON.parse(s) : initial; }
    catch { return initial; }
  });
  useEffect(() => { try { localStorage.setItem(key, JSON.stringify(state)); } catch {} }, [key, state]);
  return [state, setState];
}

const ALL_COLUMNS = [
  { key: "thumb",           label: "Image",         size: 70,  minSize: 70,  maxSize: 100, enableResizing: false },
  { key: "id",              label: "ID",            size: 180 },
  { key: "DistributorCode", label: "Distributor Code",   size: 200 },
  { key: "ImageUrl",        label: "Image URL",     size: 360 },
  { key: "hasImage",        label: "Has image",     size: 120 },
];

const DEFAULT_VISIBLE = ["thumb", "DistributorCode", "ImageUrl", "hasImage"];



export default function ProductImagesList({
  images,
  loading,
  globalFilter = "",
  onEdit,
  onDelete, 
  onOpenImage,
}) {
  const baseCols = useMemo(() => {
    const serverBase = "https://modularobjects.com/products";

    const imageCell = ({ row }) => {
      const id = row.original?.id;
      const src = id ? `${serverBase}/api/product/images/${encodeURIComponent(id)}/image` : null;
      const [err, setErr] = useState(false);
      return (
  <button
    type="button"
    className="w-12 h-12 rounded overflow-hidden bg-neutral-100 flex items-center justify-center hover:ring-2 hover:ring-blue-400"
    onClick={() => src && !err && onOpenImage?.(src)}
    title="Click to preview"
  >
    {src && !err ? (
      <img src={src} alt="" className="w-full h-full object-cover" onError={() => setErr(true)} />
    ) : (
      <span className="text-[10px] text-gray-500 px-1">no img</span>
    )}
  </button>
);
    };

    return ALL_COLUMNS.map(c => {
      if (c.key === "thumb") {
        return {
          id: "thumb",
          header: c.label,
          size: c.size, minSize: c.minSize, maxSize: c.maxSize,
          enableResizing: c.enableResizing === false ? false : true,
          enableSorting: false,
          enableColumnFilter: false,
          cell: imageCell,
        };
      }
      if (c.key === "hasImage") {
        return {
          accessorKey: "hasImage",
          header: c.label,
          size: c.size, minSize: 100, maxSize: 150,
          cell: ({ getValue }) => (Number(getValue()) ? "Yes" : "No"),
          filterFn: (row, colId, value) => {
            if (value === "") return true;
            const v = Number(row.getValue(colId)) ? "1" : "0";
            return v === value;
          },
        };
      }
      return {
        accessorKey: c.key,
        header: c.label,
        size: c.size || 160,
        minSize: 90,
        maxSize: 500,
        cell: (info) => String(info.getValue() ?? ""),
        filterFn: "includesString",
      };
    });
  }, []);

  const columns = useMemo(() => [
    ...baseCols,
    {
      id: "actions",
      header: "Actions",
      size: 120, minSize: 120, maxSize: 200,
      enableResizing: false,
      enableColumnFilter: false,
      cell: ({ row }) => (
        <div className="flex gap-2">
          <button className="bg-blue-500 text-white px-2 py-1 rounded text-xs hover:bg-blue-600"
                  onClick={() => onEdit?.(row.original)}>✏️</button>
          <button className="bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600"
                  onClick={() => onDelete?.(row.original.id)}>🗑️</button>
        </div>
        
      ),
    },
  ], [baseCols, onEdit, onDelete]);

  const [columnVisibility, setColumnVisibility] = useLocalStorage(
    "images.v1.colVisibility",
    Object.fromEntries(ALL_COLUMNS.map(c => [c.key, DEFAULT_VISIBLE.includes(c.key)]))
  );
  const [columnOrder, setColumnOrder] = useLocalStorage(
    "images.v1.colOrder",
    [...DEFAULT_VISIBLE, ...ALL_COLUMNS.map(c => c.key).filter(k => !DEFAULT_VISIBLE.includes(k)), "actions"]
  );
  const [columnSizing, setColumnSizing] = useLocalStorage("images.v1.colSizing", {});
  const [columnSizingInfo, setColumnSizingInfo] = useState({});
  const [sorting, setSorting] = useState([{ id: "DistributorCode", desc: false }]);
  const [columnFilters, setColumnFilters] = useLocalStorage("images.v1.colFilters", []);
  const [global, setGlobal] = useState(globalFilter ?? "");
  useEffect(() => { setGlobal(globalFilter ?? ""); }, [globalFilter]);

  const table = useReactTable({
    data: images || [],
    columns,
    state: { sorting, globalFilter: global, columnFilters, columnVisibility, columnOrder, columnSizing, columnSizingInfo },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobal,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    onColumnSizingInfoChange: setColumnSizingInfo,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    columnResizeMode: "onChange",
    getRowId: (row) => row.id ?? row.DistributorCode ?? Math.random().toString(36).slice(2),
  });

  const [openCols, setOpenCols] = useState(false);
  const ordered = columnOrder
    .map(k => (k === "actions" ? { key: "actions", label: "Actions" } : ALL_COLUMNS.find(c => c.key === k)))
    .filter(Boolean);

  const moveCol = (key, dir) => {
    setColumnOrder(prev => {
      const next = [...prev];
      const i = next.indexOf(key);
      if (i === -1) return prev;
      const j = dir === "left" ? i - 1 : i + 1;
      if (j < 0 || j >= next.length) return prev;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  if (loading) return <div className="text-center py-4">Loading images...</div>;

  return (
    <div className="space-y-3">
      {/* toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <input
          value={global}
          onChange={(e) => table.setGlobalFilter(e.target.value)}
          placeholder="Search all columns…"
          className="px-3 py-2 border rounded w-full max-w-md dark:bg-neutral-800 dark:text-white"
        />
        <button
          onClick={() => setOpenCols(o => !o)}
          className="px-3 py-2 rounded border hover:bg-gray-50 dark:hover:bg-neutral-800"
        >
          Columns
        </button>
        
        {openCols && (
          <div className="z-20 border rounded shadow bg-white dark:bg-neutral-900 p-3 max-h-96 overflow-auto">
            {ordered.map(c => (
              <div key={c.key} className="flex items-center justify-between py-1 gap-3">
                <label className="flex items-center gap-2">
                  {c.key !== "actions" && (
                    <input
                      type="checkbox"
                      checked={!!columnVisibility[c.key]}
                      onChange={() => setColumnVisibility(v => ({ ...v, [c.key]: !v[c.key] }))}
                    />
                  )}
                  {c.label}
                </label>
                <div className="flex gap-1">
                  <button className="px-2 py-0.5 border rounded text-xs" onClick={() => moveCol(c.key, "left")}>◀</button>
                  <button className="px-2 py-0.5 border rounded text-xs" onClick={() => moveCol(c.key, "right")}>▶</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="overflow-auto border rounded max-h-[70vh]">
        <table className="min-w-full border-collapse table-fixed">
          <thead className="bg-gray-100 dark:bg-neutral-800 sticky top-0 z-10">
            {table.getHeaderGroups().map(hg => (
              <tr key={hg.id}>
                {hg.headers.map(header => {
                  const canResize = header.column.getCanResize();
                  const isResizing = header.column.getIsResizing();
                  const size = header.column.getSize();
                  return (
                    <th
                      key={header.id}
                      className="px-3 py-2 border text-left whitespace-nowrap cursor-pointer select-none relative"
                      onClick={header.column.getToggleSortingHandler()}
                      style={{ width: size, maxWidth: size, minWidth: size }}
                      title="Click to sort. Drag right edge to resize."
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getIsSorted() === "asc" ? " ↑" : header.column.getIsSorted() === "desc" ? " ↓" : ""}
                      {canResize && (
                        <div
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          className={`absolute top-0 right-0 h-full w-1 cursor-col-resize select-none touch-none ${isResizing ? "bg-blue-500" : "bg-transparent"}`}
                        />
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}

            {/* kolona filteri */}
            <tr>
              {table.getHeaderGroups()[0].headers.map(header => {
                const col = header.column;
                if (col.id === "actions" || col.id === "thumb") {
                  return <th key={col.id} className="px-2 py-1 border" />;
                }
                if (col.id === "hasImage") {
                  const v = (col.getFilterValue() ?? "") + "";
                  return (
                    <th key={col.id} className="px-2 py-1 border">
                      <select
                        className="w-full px-2 py-1 border rounded text-sm dark:bg-neutral-800 dark:text-white"
                        value={v}
                        onChange={(e) => col.setFilterValue(e.target.value)}
                      >
                        <option value="">All</option>
                        <option value="1">Yes</option>
                        <option value="0">No</option>
                      </select>
                    </th>
                  );
                }
                return (
                  <th key={col.id} className="px-2 py-1 border">
                    <input
                      className="w-full px-2 py-1 border rounded text-sm dark:bg-neutral-800 dark:text-white"
                      value={String(col.getFilterValue() ?? "")}
                      onChange={(e) => col.setFilterValue(e.target.value)}
                      placeholder="Filter…"
                    />
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {table.getRowModel().rows.map(row => (
              <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-neutral-800">
                {row.getVisibleCells().map(cell => {
                  const w = cell.column.getSize();
                  return (
                    <td
                      key={cell.id}
                      className="px-3 py-2 border whitespace-nowrap overflow-hidden text-ellipsis"
                      style={{ width: w, maxWidth: w, minWidth: w }}
                      title={String(cell.getValue() ?? "")}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  );
                })}
              </tr>
            ))}
            {table.getRowModel().rows.length === 0 && (
              <tr>
                <td className="px-3 py-6 text-center text-gray-500" colSpan={table.getVisibleFlatColumns().length}>
                  No images found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
